import { useEffect, useCallback, useMemo, useState, useRef, type CSSProperties } from 'react';
import { WelcomeScreen } from '../components/WelcomeScreen';
import { PatternGrid } from '../components/PatternGrid';
import { MelodySidePanels } from '../components/MelodySidePanels';
import { MelodyMeasureBar } from '../components/MelodyMeasureBar';
import { TransportBar } from '../components/TransportBar';
import { SettingsPanel } from '../components/SettingsPanel';
import { MeasurePageView } from '../components/MeasurePageView';
import { usePlayback } from '../hooks/usePlayback';
import { useMeasurePagination } from '../hooks/useMeasurePagination';
import { useMelodyTwoMeasureLayout } from '../hooks/useMelodyTwoMeasureLayout';
import { usePresentationMode } from '../../contexts/PresentationModeContext';
import { DrumAccompanimentPanel } from '../components/DrumAccompanimentPanel';
import { MelodyLibraryPanel } from '../components/MelodyLibraryPanel';
import { MeasureChordPanel } from '../components/MeasureChordPanel';
import { MixVolumePanel } from '../components/MixVolumePanel';
import { celestialPadEngine, unlockCelestialPad } from '../audio/celestialPadEngine';
import { preloadDrumSamples } from '../audio/drumSamples';
import { preloadBoomwhackerSamples } from '../audio/boomwhackerSamples';
import { ensureBoomVexFonts } from '../lib/boomVexFonts';
import { DEFAULT_NOTE_COLORS } from '../constants';
import {
  applyFigureToBeat,
  clonePattern,
  createEmptyPattern,
  removeMeasureAt,
  resizePattern,
} from '../utils/pattern';
import { loadMelodyLibrary, type SavedMelodyPattern } from '../utils/melodyLibrary';
import {
  MELODY_MEASURES_SHIFT,
  MELODY_MEASURES_VISIBLE,
  pageForWindowStart,
  totalSlidingPages,
} from '../utils/measurePages';
import {
  clearDrumRow,
  createEmptyDrumPattern,
  resizeDrumPattern,
  toggleDrumStep,
} from '../utils/drumPattern';
import {
  clearChordRow,
  createEmptyChordAccompaniment,
  measureChordSummaries,
  resizeChordAccompaniment,
  extendChordRange,
  toggleChordStep,
} from '../utils/chordAccompaniment';
import {
  applyMixVolumes,
  channelGain,
  loadMixVolumes,
  type MixVolumes,
} from '../utils/mixVolumes';
import type {
  CellShape,
  ChordAccompaniment,
  ChordId,
  DrumId,
  DrumPattern,
  NoteColors,
  NoteFigure,
  NoteId,
  Pattern,
} from '../types';

const STORAGE_COLORS = 'boom-note-colors';
const STORAGE_SHAPE = 'boom-cell-shape';
const STORAGE_SOUND = 'boom-sound-enabled';
const STORAGE_DRUM = 'boom-drum-pattern';
const STORAGE_CHORDS = 'boom-chord-accompaniment';
const MAX_MEASURES = 8;

interface Props {
  onBack: () => void;
}

function loadSoundEnabled(): boolean {
  return localStorage.getItem(STORAGE_SOUND) !== 'false';
}

function loadColors(): NoteColors {
  try {
    const raw = localStorage.getItem(STORAGE_COLORS);
    if (raw) return { ...DEFAULT_NOTE_COLORS, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_NOTE_COLORS };
}

function loadDrumPattern(): DrumPattern {
  try {
    const raw = localStorage.getItem(STORAGE_DRUM);
    if (raw) {
      const parsed = JSON.parse(raw) as DrumPattern;
      return resizeDrumPattern(
        { ...createEmptyDrumPattern(parsed.measures), ...parsed },
        parsed.measures ?? 1,
      );
    }
  } catch {
    /* ignore */
  }
  return createEmptyDrumPattern(1);
}

function loadChordAccompaniment(measureCount: number): ChordAccompaniment {
  try {
    const raw = localStorage.getItem(STORAGE_CHORDS);
    if (raw) {
      const parsed = JSON.parse(raw) as ChordAccompaniment;
      return resizeChordAccompaniment(parsed, parsed.measures?.length ?? measureCount);
    }
  } catch {
    /* ignore */
  }
  return createEmptyChordAccompaniment(measureCount);
}

function loadShape(): CellShape {
  return localStorage.getItem(STORAGE_SHAPE) === 'square' ? 'square' : 'circle';
}

export function MelodyApp({ onBack }: Props) {
  const [showWelcome, setShowWelcome] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [colors, setColors] = useState<NoteColors>(loadColors);
  const [shape, setShape] = useState<CellShape>(loadShape);
  const [selectedNote, setSelectedNote] = useState<NoteId>('do');
  const [selectedFigure, setSelectedFigure] = useState<NoteFigure>('semicorchea');
  const [bpm, setBpm] = useState(80);
  const [measureCount, setMeasureCount] = useState(2);
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
  const [soundEnabled, setSoundEnabled] = useState(loadSoundEnabled);
  const appRef = useRef<HTMLDivElement>(null);
  const [pattern, setPattern] = useState<Pattern>(() => createEmptyPattern(2, 4));
  const [drumPattern, setDrumPattern] = useState<DrumPattern>(loadDrumPattern);
  const [chordAccompaniment, setChordAccompaniment] = useState<ChordAccompaniment>(() =>
    loadChordAccompaniment(2),
  );
  const [drumPanelOpen, setDrumPanelOpen] = useState(false);
  const [mixPanelOpen, setMixPanelOpen] = useState(false);
  const [mixVolumes, setMixVolumes] = useState<MixVolumes>(() => loadMixVolumes());
  const [chordPanelMeasureIndex, setChordPanelMeasureIndex] = useState<number | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryCount, setLibraryCount] = useState(() => loadMelodyLibrary().length);
  const [loadedLibraryId, setLoadedLibraryId] = useState<string | null>(null);

  const { playing, activeIndex, activeMeasureIndex, activeDrumStep, play, stop } =
    usePlayback(pattern, bpm, soundEnabled, drumPattern, chordAccompaniment, mixVolumes);

  const handleMixVolumesChange = useCallback((next: MixVolumes) => {
    const applied = applyMixVolumes(next);
    setMixVolumes(applied);
    celestialPadEngine.setHarmonyMixGain(channelGain('harmony', applied));
  }, []);

  useEffect(() => {
    celestialPadEngine.setHarmonyMixGain(channelGain('harmony', mixVolumes));
  }, [mixVolumes]);

  const playView = playing;
  const { setPresentation } = usePresentationMode();
  const playViewportRef = useRef<HTMLDivElement>(null);
  const editViewportRef = useRef<HTMLDivElement>(null);
  const canvasViewportRef = playView ? playViewportRef : editViewportRef;
  const canvasLayout = useMelodyTwoMeasureLayout(canvasViewportRef, appRef, !showWelcome);

  useEffect(() => {
    setPresentation(playView);
    return () => setPresentation(false);
  }, [playView, setPresentation]);

  const paginationOpts = useMemo(
    () => ({ visible: MELODY_MEASURES_VISIBLE, shift: MELODY_MEASURES_SHIFT }),
    [],
  );

  const { page, pages, slide, windowStart, visibleIndices } = useMeasurePagination(
    measureCount,
    activeMeasureIndex,
    playView,
    paginationOpts,
  );

  const [editWindowStart, setEditWindowStart] = useState(0);
  const editPages = totalSlidingPages(
    measureCount,
    MELODY_MEASURES_VISIBLE,
    MELODY_MEASURES_SHIFT,
  );
  const editPage = pageForWindowStart(editWindowStart, MELODY_MEASURES_SHIFT);
  const editVisibleIndices = useMemo(
    () =>
      Array.from({ length: MELODY_MEASURES_VISIBLE }, (_, i) => editWindowStart + i).filter(
        (i) => i < measureCount,
      ),
    [editWindowStart, measureCount],
  );

  useEffect(() => {
    if (measureCount <= MELODY_MEASURES_VISIBLE) {
      setEditWindowStart(0);
      return;
    }
    const maxStart = measureCount - MELODY_MEASURES_VISIBLE;
    if (editWindowStart > maxStart) {
      setEditWindowStart(Math.max(0, maxStart));
    }
  }, [measureCount, editWindowStart]);

  const [slideUi, setSlideUi] = useState(slide);
  useEffect(() => setSlideUi(slide), [slide, windowStart]);

  const clearSlide = useCallback(() => setSlideUi('none'), []);

  const displayMeasures = playView ? visibleIndices : editVisibleIndices;

  const goEditPrevPage = useCallback(() => {
    setEditWindowStart((start) => Math.max(0, start - MELODY_MEASURES_SHIFT));
  }, []);

  const goEditNextPage = useCallback(() => {
    setEditWindowStart((start) => {
      const maxStart = Math.max(0, measureCount - MELODY_MEASURES_VISIBLE);
      return Math.min(maxStart, start + MELODY_MEASURES_SHIFT);
    });
  }, [measureCount]);

  useEffect(() => {
    localStorage.setItem(STORAGE_COLORS, JSON.stringify(colors));
  }, [colors]);

  useEffect(() => {
    localStorage.setItem(STORAGE_SHAPE, shape);
  }, [shape]);

  useEffect(() => {
    localStorage.setItem(STORAGE_SOUND, String(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    void ensureBoomVexFonts();
    void preloadBoomwhackerSamples();
    void preloadDrumSamples();
    void unlockCelestialPad();
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_DRUM, JSON.stringify(drumPattern));
  }, [drumPattern]);

  useEffect(() => {
    localStorage.setItem(STORAGE_CHORDS, JSON.stringify(chordAccompaniment));
  }, [chordAccompaniment]);

  const syncLayout = useCallback((nextBeats: number, nextMeasures: number) => {
    setPattern((prev) => resizePattern(prev, nextMeasures, nextBeats));
    setMeasureCount(nextMeasures);
    setDrumPattern((prev) => resizeDrumPattern(prev, nextMeasures));
    setChordAccompaniment((prev) => resizeChordAccompaniment(prev, nextMeasures));
  }, []);

  const handleMeasureCountChange = useCallback(
    (n: number) => {
      const next = Math.min(MAX_MEASURES, Math.max(1, n));
      syncLayout(beatsPerMeasure, next);
    },
    [beatsPerMeasure, syncLayout],
  );

  const handleAddMeasure = useCallback(() => {
    if (measureCount >= MAX_MEASURES) return;
    const next = measureCount + 1;
    handleMeasureCountChange(next);
    if (next > MELODY_MEASURES_VISIBLE) {
      const newIndex = next - 1;
      const targetStart = Math.floor(newIndex / MELODY_MEASURES_SHIFT) * MELODY_MEASURES_SHIFT;
      const maxStart = Math.max(0, next - MELODY_MEASURES_VISIBLE);
      setEditWindowStart(Math.min(targetStart, maxStart));
    }
  }, [handleMeasureCountChange, measureCount]);

  const handleDeleteMeasure = useCallback(
    (measureIndex: number) => {
      if (measureCount <= 1) return;
      setPattern((prev) => removeMeasureAt(prev, measureIndex));
      setDrumPattern((prev) => ({
        ...prev,
        measures: prev.measures.filter((_, i) => i !== measureIndex),
      }));
      setChordAccompaniment((prev) => ({
        ...prev,
        measures: prev.measures.filter((_, i) => i !== measureIndex),
      }));
      setMeasureCount((c) => Math.max(1, c - 1));
    },
    [measureCount],
  );

  const handleApplyFigure = (measureIndex: number, beatIndex: number) => {
    setPattern((prev) => ({
      ...prev,
      measures: prev.measures.map((m, mi) =>
        mi !== measureIndex
          ? m
          : applyFigureToBeat(m, beatIndex, selectedFigure, prev.beatsPerMeasure),
      ),
    }));
  };

  const handleApplyFigureWithFigure = useCallback(
    (measureIndex: number, beatIndex: number, figure: NoteFigure) => {
      setSelectedFigure(figure);
      setPattern((prev) => ({
        ...prev,
        measures: prev.measures.map((m, mi) =>
          mi !== measureIndex
            ? m
            : applyFigureToBeat(m, beatIndex, figure, prev.beatsPerMeasure),
        ),
      }));
    },
    [],
  );

  const handleCellChange = (
    measureIndex: number,
    beatIndex: number,
    cellIndex: number,
    note: NoteId,
  ) => {
    setPattern((prev) => ({
      ...prev,
      measures: prev.measures.map((m, mi) => {
        if (mi !== measureIndex) return m;
        return {
          slots: m.slots.map((slot, bi) => {
            if (bi !== beatIndex || !slot.active) return slot;
            return {
              ...slot,
              cells: slot.cells.map((c, ci) =>
                ci === cellIndex ? { note } : c,
              ),
            };
          }),
        };
      }),
    }));
  };

  const handleDrumMeasuresChange = useCallback((measures: number) => {
    setDrumPattern((prev) => resizeDrumPattern(prev, measures));
  }, []);

  const handleDrumToggleStep = useCallback((drum: DrumId, stepIndex: number) => {
    setDrumPattern((prev) => {
      const next = toggleDrumStep(prev, drum, stepIndex);
      return { ...next, enabled: true };
    });
  }, []);

  const handleDrumClearRow = useCallback((drum: DrumId) => {
    setDrumPattern((prev) => clearDrumRow(prev, drum));
  }, []);

  const handleChordToggleStep = useCallback(
    (measureIndex: number, chordId: ChordId, stepIndex: number) => {
      setChordAccompaniment((prev) => toggleChordStep(prev, measureIndex, chordId, stepIndex));
    },
    [],
  );

  const handleChordExtendRange = useCallback(
    (measureIndex: number, chordId: ChordId, fromStep: number, toStep: number) => {
      setChordAccompaniment((prev) =>
        extendChordRange(prev, measureIndex, chordId, fromStep, toStep),
      );
    },
    [],
  );

  const handleChordClearRow = useCallback((measureIndex: number, chordId: ChordId) => {
    setChordAccompaniment((prev) => clearChordRow(prev, measureIndex, chordId));
  }, []);

  const chordMeasureSummaries = useMemo(
    () => measureChordSummaries(chordAccompaniment),
    [chordAccompaniment],
  );

  const activeChordStep = useMemo(() => {
    if (!playing || activeMeasureIndex === null || chordPanelMeasureIndex !== activeMeasureIndex) {
      return null;
    }
    return activeDrumStep !== null ? activeDrumStep % 16 : null;
  }, [playing, activeMeasureIndex, chordPanelMeasureIndex, activeDrumStep]);

  const refreshLibraryCount = useCallback(() => {
    setLibraryCount(loadMelodyLibrary().length);
  }, []);

  const handleLoadFromLibrary = useCallback(
    (entry: SavedMelodyPattern) => {
      stop();
      setPattern(clonePattern(entry.pattern));
      setBpm(entry.bpm);
      setMeasureCount(entry.pattern.measures.length);
      setBeatsPerMeasure(entry.pattern.beatsPerMeasure);
      if (entry.drumPattern) {
        setDrumPattern(
          resizeDrumPattern(
            { ...createEmptyDrumPattern(entry.drumPattern.measures), ...entry.drumPattern },
            entry.drumPattern.measures,
          ),
        );
      } else {
        setDrumPattern(createEmptyDrumPattern(1));
      }
      if (entry.chordAccompaniment) {
        setChordAccompaniment(
          resizeChordAccompaniment(entry.chordAccompaniment, entry.pattern.measures.length),
        );
      } else {
        setChordAccompaniment(createEmptyChordAccompaniment(entry.pattern.measures.length));
      }
      setLoadedLibraryId(entry.id);
    },
    [stop],
  );

  const handleLibrarySaved = useCallback(
    (entry: SavedMelodyPattern) => {
      setLoadedLibraryId(entry.id);
      refreshLibraryCount();
    },
    [refreshLibraryCount],
  );

  const handleLibraryDeleted = useCallback(
    (id: string) => {
      if (loadedLibraryId === id) setLoadedLibraryId(null);
      refreshLibraryCount();
    },
    [loadedLibraryId, refreshLibraryCount],
  );

  const cssVars = useMemo(
    () =>
      ({
        '--color-do': colors.do,
        '--color-re': colors.re,
        '--color-mi': colors.mi,
        '--color-fa': colors.fa,
        '--color-sol': colors.sol,
        '--color-la': colors.la,
        '--color-si': colors.si,
        '--color-rest': colors.rest,
        '--accent': colors.sol,
        '--melody-play-cols': canvasLayout.cols,
        '--melody-play-rows': canvasLayout.rows,
      }) as CSSProperties,
    [colors, canvasLayout.cols, canvasLayout.rows],
  );

  const grid = (
    <PatternGrid
      pattern={pattern}
      colors={colors}
      shape={shape}
      activeIndex={activeIndex}
      selectedNote={selectedNote}
      selectedFigure={selectedFigure}
      visibleMeasures={displayMeasures}
      variant="melody"
      layout="row"
      readOnly={playView}
      onCellChange={handleCellChange}
      onApplyFigure={handleApplyFigure}
      onApplyFigureWithFigure={handleApplyFigureWithFigure}
      onDeleteMeasure={playView ? undefined : handleDeleteMeasure}
      chordMeasureSummaries={chordMeasureSummaries}
      onOpenChordMeasure={playView ? undefined : setChordPanelMeasureIndex}
    />
  );

  return (
    <div
      ref={appRef}
      className={`app app--melody ${playView ? 'app--melody-play app--paginated app--presentation' : 'app--melody-edit'}`}
      style={cssVars}
    >
      {showWelcome && (
        <WelcomeScreen onContinue={() => setShowWelcome(false)} />
      )}

      <header className="app-header">
        <div>
          <button type="button" className="btn-back" onClick={onBack}>
            ← Inicio
          </button>
          <h1 className="app-title">Boom Patrones</h1>
          <p className="app-subtitle">Jefferson School · 1ro básico</p>
        </div>
        {!playView && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setSettingsOpen(true)}
          >
            ⚙ Configuración
          </button>
        )}
      </header>

      {!playView && (
        <div className="melody-intro">
          <p className="hint hint--melody">
            Pulsa la <strong>figura</strong> de un tiempo para cambiarla.
            Elige <strong>nota</strong> en el panel y pinta en el lienzo.
            Usa <strong>Armonía</strong> en cada compás para los acordes.
          </p>
          <button
            type="button"
            className="btn btn-settings-mini"
            onClick={() => setSettingsOpen(true)}
            title="Configuración"
            aria-label="Configuración"
          >
            ⚙
          </button>
        </div>
      )}

      {playView ? (
        <div className="melody-play-stage">
          <MeasurePageView
            page={page}
            pages={pages}
            slide={slideUi}
            onSlideEnd={clearSlide}
            showPager={measureCount > MELODY_MEASURES_VISIBLE}
            measureCount={measureCount}
            windowStart={windowStart}
            visibleCount={MELODY_MEASURES_VISIBLE}
          >
            <div ref={playViewportRef} className="melody-play-stage__viewport">
              {grid}
            </div>
          </MeasurePageView>
        </div>
      ) : (
        <div className="melody-workspace">
          <MelodySidePanels
            colors={colors}
            selectedNote={selectedNote}
            onNoteSelect={setSelectedNote}
            soundEnabled={soundEnabled}
          />
          <div className="melody-canvas melody-canvas--paginated">
            <MelodyMeasureBar
              measureCount={measureCount}
              maxMeasures={MAX_MEASURES}
              onMeasureCountChange={handleMeasureCountChange}
              onAddMeasure={handleAddMeasure}
              onOpenLibrary={() => setLibraryOpen(true)}
              libraryCount={libraryCount}
            />
            <MeasurePageView
              page={editPage}
              pages={editPages}
              slide="none"
              onSlideEnd={() => {}}
              showPager={measureCount > MELODY_MEASURES_VISIBLE}
              measureCount={measureCount}
              windowStart={editWindowStart}
              visibleCount={MELODY_MEASURES_VISIBLE}
              onPrevPage={goEditPrevPage}
              onNextPage={goEditNextPage}
            >
              <div ref={editViewportRef} className="melody-canvas__strip" aria-label="Compases del patrón">
                {grid}
              </div>
            </MeasurePageView>
          </div>
        </div>
      )}

      <TransportBar
        bpm={bpm}
        onBpmChange={setBpm}
        figure={selectedFigure}
        onFigureChange={setSelectedFigure}
        playing={playing}
        onPlay={play}
        onStop={stop}
        soundEnabled={soundEnabled}
        onSoundToggle={() => setSoundEnabled((s) => !s)}
        variant="melody"
        dock="bottom"
        hideFullscreen
        drumEnabled={drumPattern.enabled}
        onDrumAccompaniment={() => setDrumPanelOpen(true)}
        onOpenMixVolumes={() => setMixPanelOpen(true)}
      />

      <SettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        colors={colors}
        onColorsChange={setColors}
        shape={shape}
        onShapeChange={setShape}
        measureCount={measureCount}
        onMeasureCountChange={handleMeasureCountChange}
        beatsPerMeasure={beatsPerMeasure}
        onBeatsChange={(n) => {
          setBeatsPerMeasure(n);
          syncLayout(n, measureCount);
        }}
        onShowWelcome={() => {
          setSettingsOpen(false);
          setShowWelcome(true);
        }}
      />

      <MixVolumePanel
        open={mixPanelOpen}
        volumes={mixVolumes}
        onChange={handleMixVolumesChange}
        onClose={() => setMixPanelOpen(false)}
      />

      <DrumAccompanimentPanel
        open={drumPanelOpen}
        pattern={drumPattern}
        activeStep={playing ? activeDrumStep : null}
        onClose={() => setDrumPanelOpen(false)}
        onPatternChange={setDrumPattern}
        onToggleStep={handleDrumToggleStep}
        onClearRow={handleDrumClearRow}
        onMeasuresChange={handleDrumMeasuresChange}
      />

      <MeasureChordPanel
        open={chordPanelMeasureIndex !== null}
        measureIndex={chordPanelMeasureIndex ?? 0}
        bpm={bpm}
        accompaniment={chordAccompaniment}
        activeStep={activeChordStep}
        onClose={() => setChordPanelMeasureIndex(null)}
        onAccompanimentChange={setChordAccompaniment}
        onToggleStep={handleChordToggleStep}
        onExtendRange={handleChordExtendRange}
        onClearRow={handleChordClearRow}
      />

      <MelodyLibraryPanel
        open={libraryOpen}
        bpm={bpm}
        pattern={pattern}
        drumPattern={drumPattern}
        chordAccompaniment={chordAccompaniment}
        loadedId={loadedLibraryId}
        onClose={() => setLibraryOpen(false)}
        onLoad={handleLoadFromLibrary}
        onSaved={handleLibrarySaved}
        onDeleted={handleLibraryDeleted}
      />
    </div>
  );
}
